import { useCallback, useRef, useState } from "react"

// Busy/error/reset/guarded-close shape shared by new-dashboard-dialog.tsx and save-as-new-dialog.tsx.
// Excludes name/layout-selection state — that differs per dialog. [APP-3309]
export interface DialogFormState {
  isBusy: boolean
  error: string | null
  setError: (error: string | null) => void
  // Synchronous re-entry latch: returns false (no-op) if already busy, else flips busy and returns
  // true. Call at the top of a submit handler, after its own name/selection guards.
  beginBusy: () => boolean
  // Call on a failed submit — clears busy and sets the inline retry error.
  endBusy: (errorMessage?: string) => void
  // Clears busy + error (not caller-owned fields like name) — call on a successful submit.
  reset: () => void
  // Ignores close while busy so an in-flight submit is never stranded.
  guardedClose: (onClose: () => void) => void
}

export function useDialogFormState(): DialogFormState {
  const [isBusy, setIsBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const isBusyRef = useRef(false)

  const beginBusy = useCallback(() => {
    if (isBusyRef.current) return false
    isBusyRef.current = true
    setIsBusy(true)
    setError(null)
    return true
  }, [])

  const endBusy = useCallback((errorMessage?: string) => {
    isBusyRef.current = false
    setIsBusy(false)
    if (errorMessage !== undefined) setError(errorMessage)
  }, [])

  const reset = useCallback(() => {
    endBusy()
    setError(null)
  }, [endBusy])

  const guardedClose = useCallback((onClose: () => void) => {
    // Reads the ref, not isBusy state, so a same-tick close can't slip past a just-started submit.
    if (isBusyRef.current) return
    onClose()
  }, [])

  return { isBusy, error, setError, beginBusy, endBusy, reset, guardedClose }
}
